import type { BlockKey } from './clientPage'
import { countOf, piecesFromExtras, streamDays, type ExtraDetail, type ExtraKey } from './clientExtras'

/**
 * SETTING UP A CLIENT PAGE.
 *
 * **A tick never stands alone** — ticking the dates asks for them, ticking
 * the tracker writes a starter one.
 *
 * **Something else is a pick-list of its own (16 Oct 2026).** It was one
 * option asking how many and what shape; now it opens a short list —
 * cutdowns, trailer, graphics, soundbites, live stream, or name it
 * yourself — and **several can be ticked at once**, each getting its own
 * screen. See utils/clientExtras.ts for what each one asks and why.
 *
 * **Say the thing, don't describe it, and say it the same way twice.** "A
 * plan they can follow" became *Project tracker*; the option labels lost
 * their stray articles; and one object has one name — *pieces* in the
 * studio, *tiles* only when describing the page's layout.
 *
 * **What each screen asks is chosen by what it saves later.** Four things
 * earn their place, and nothing else does:
 *
 *   WHERE IT IS SHOT   one field, read three times — the calendar entry,
 *                      the call sheet, and the client's dates block.
 *   MORE THAN ONE DAY  ATM was a build day, a tech day and four event days.
 *   WHEN IT IS DUE     `deliverables.due_date` already exists and the
 *                      client page already draws *Editing, due 16 November*
 *                      on a tile. Nothing populated it.
 *   WHAT SHAPE         `video_format` is set one piece at a time by hand
 *                      today. For a social cutdown it is the whole point.
 *
 * Deliberately NOT asked: interviewees, runtimes, crew, kit, budgets. They
 * are unknown at setup, or they change, or they are the call sheet's job.
 * Everything here is optional and nothing blocks Continue — which is why
 * no single field is labelled "optional" any more: marking two of them
 * implied the rest were required.
 */

export type KindKey = 'films' | 'podcast' | 'photography' | 'event' | 'other'

export interface KindSpec {
  key: KindKey
  label: string
  pieces: string
  count: number
  category: string
  /** Shape only means something for moving pictures. */
  shaped: boolean
  /** Only where the name alone doesn't say it. Kept to a few words. */
  hint?: string
  naming: (index: number, total: number) => string
}

/**
 * **FILMS ARE FILMS, NOT EPISODES (17 Oct 2026).** Two films used to come
 * out of setup named *Episode 1* and *Episode 2* — the same words Podcast
 * uses, on a page where the heading says Films. A client opening their
 * page saw episodes of something nobody had told them was a series. The
 * single-film case already said *The film*; the plural case now agrees
 * with it. Podcast keeps Episode, which is what an episode is.
 */
export const KINDS: KindSpec[] = [
  { key: 'films', label: 'Films', pieces: 'films', count: 1, category: 'Video', shaped: true,
    naming: (i, total) => (total === 1 ? 'The film' : `Film ${i + 1}`) },
  { key: 'podcast', label: 'Podcast', pieces: 'episodes', count: 6, category: 'Video', shaped: true,
    naming: (i) => `Episode ${i + 1}` },
  { key: 'event', label: 'Event coverage', pieces: 'films', count: 2, category: 'Video', shaped: true,
    naming: (i, total) => (total === 1 ? 'The event film' : `Event film ${i + 1}`) },
  { key: 'photography', label: 'Photography', pieces: 'sets', count: 1, category: 'Photography', shaped: false,
    naming: (i, total) => (total === 1 ? 'The photographs' : `Set ${i + 1}`) },
  { key: 'other', label: 'Something else', pieces: 'pieces', count: 2, category: 'Video', shaped: true,
    hint: 'Cutdowns, a trailer, graphics, a live stream',
    naming: (i) => `Piece ${i + 1}` },
]

export const kindSpec = (kind: KindKey): KindSpec =>
  KINDS.find((item) => item.key === kind) ?? KINDS[0]

/** "1 film", "2 films". Defined in clientExtras.ts; see the note there. */
export { countOf }

export const COUNT_CHOICES = [1, 2, 3, 4, 5, 6, 8, 10, 12, 16, 20, 24]

/** The two shapes Portal actually delivers, and the common case of both. */
export type Shape = 'landscape' | 'vertical' | 'both'

export const SHAPES: { key: Shape; label: string }[] = [
  { key: 'landscape', label: 'Landscape' },
  { key: 'vertical', label: 'Vertical' },
  { key: 'both', label: 'Both' },
]

export const FORMAT_NAME: Record<string, string> = {
  landscape: '16:9 Landscape',
  vertical: '9:16 Vertical',
}

export interface KindDetail {
  count: number
  ongoing: boolean
  /** Podcast only. Filmed is the default; audio-only is the exception. */
  audioOnly: boolean
  /** Unused since Something else became its own list. Kept for old drafts. */
  named: string
  /** ISO date, or '' — written to every piece of this kind. */
  due: string
  shape: Shape
}

export const BLANK_DETAIL: KindDetail = {
  count: 1, ongoing: false, audioOnly: false, named: '', due: '', shape: 'landscape',
}

export function detailOf(answers: SetupAnswers, kind: KindKey): KindDetail {
  return answers.details[kind] ?? { ...BLANK_DETAIL, count: kindSpec(kind).count }
}

/** What this kind is called on screen. */
export function kindTitle(detail: KindDetail, spec: KindSpec): string {
  return spec.key === 'other' && detail.named.trim() ? detail.named.trim() : spec.label
}

/**
 * WHAT THEY SEE WHILE WE SHOOT — and why the call sheet is not here.
 *
 * **A call sheet was the third option until 17 Oct 2026.** It never
 * belonged. A call sheet is not something a client sees on their page: it
 * is its own document, made in the Call Sheets tab, and the link goes
 * straight to a WhatsApp group or an email. Tom, settling it: *"The call
 * sheet is generated from that tab and a link is sent directly to either a
 * whatsapp group or emailed directly. It is different from the client
 * page."*
 *
 * The tick had been inert for weeks, then briefly made one empty sheet per
 * shoot day — which only turned a question that shouldn't be asked into a
 * second place that makes call sheets. Two places making one thing is the
 * duplication the 17 Oct workflow audit set out to remove, so both the
 * question and the writing went.
 *
 * The setup flow still feeds call sheets, just not by asking: it writes
 * the shoot days, and Call Sheet Create lists a project's days as
 * press-to-fill buttons. One place makes them; the days are already there
 * when you get to it.
 */
export type DuringKey = 'dates' | 'plan'

export const DURING: { key: DuringKey; label: string }[] = [
  { key: 'dates', label: 'Shoot dates' },
  { key: 'plan', label: 'Project tracker' },
]

export type Watching = 'approve' | 'watch' | 'files'

export const WATCHING: { key: Watching; label: string }[] = [
  { key: 'approve', label: 'Watch it and approve it' },
  { key: 'watch', label: 'Just watch it' },
  { key: 'files', label: 'Just collect the files' },
]

/** One day, or a run of days, with where it happens. */
export interface ShootDay {
  date: string
  end: string
  title: string
  location: string
}

export const BLANK_DAY: ShootDay = { date: '', end: '', title: '', location: '' }

export interface Piece {
  name: string
  kind: KindKey
  category: string
  /** The stored video_format value, or '' where shape means nothing. */
  format: string
  due: string
}

export interface Contact {
  name: string
  email: string
}

export interface SetupAnswers {
  client: string
  name: string
  kinds: KindKey[]
  details: Record<string, KindDetail>
  /** Which of the Something else list are ticked. */
  extras: ExtraKey[]
  extraDetails: Record<string, ExtraDetail>
  outline: string
  during: DuringKey[]
  shoots: ShootDay[]
  watching: Watching | ''
  contacts: Contact[]
}

export const BLANK: SetupAnswers = {
  client: '', name: '', kinds: [], details: {}, extras: [], extraDetails: {}, outline: '',
  during: [], shoots: [], watching: 'approve', contacts: [],
}

/**
 * Every piece the job starts with.
 *
 * **Both shapes means twice the pieces**, because that is what it means in
 * the edit: a landscape cut and a vertical cut are two files, two review
 * links and two approvals. The screen says so rather than doubling quietly.
 *
 * `other` is skipped in this loop — it no longer has a detail screen of
 * its own. Whatever was ticked inside it comes back from clientExtras.
 */
export function piecesFrom(answers: SetupAnswers): Piece[] {
  const out: Piece[] = []
  for (const kind of KINDS) {
    if (kind.key === 'other') continue
    if (!answers.kinds.includes(kind.key)) continue
    const detail = detailOf(answers, kind.key)
    const category = kind.key === 'podcast' && detail.audioOnly ? 'Audio' : kind.category
    const shaped = kind.shaped && category === 'Video'
    const shapes: Shape[] =
      !shaped ? ['landscape'] : detail.shape === 'both' ? ['landscape', 'vertical'] : [detail.shape]

    for (const shape of shapes) {
      for (let index = 0; index < Math.max(1, detail.count); index += 1) {
        const base = kind.naming(index, detail.count)
        out.push({
          name: shapes.length > 1 && shape === 'vertical' ? `${base} · Vertical` : base,
          kind: kind.key,
          category,
          format: shaped ? FORMAT_NAME[shape] : '',
          due: detail.due,
        })
      }
    }
  }

  if (answers.kinds.includes('other')) out.push(...piecesFromExtras(answers))
  return out
}

/** Every day the job puts on the client's schedule, shoots and streams alike. */
export function allDays(answers: SetupAnswers): ShootDay[] {
  const shoots = answers.during.includes('dates')
    ? answers.shoots.filter((day) => day.date)
    : []
  const streams = answers.kinds.includes('other') ? streamDays(answers) : []
  return [...shoots, ...streams]
}

export interface StarterStep {
  title: string
  owner: 'portal' | 'client' | 'both'
  phase: 'pre' | 'production' | 'delivery'
  start: string
}

export function starterPlan(answers: SetupAnswers): StarterStep[] {
  const days = allDays(answers)
  const first = days[0]
  const shootish = answers.kinds.some((kind) => kind !== 'other') || days.length > 0
  return [
    { title: 'Kick-off call', owner: 'both', phase: 'pre', start: '' },
    { title: 'Sign off the plan', owner: 'client', phase: 'pre', start: '' },
    ...(shootish
      ? [{
          title: first?.title.trim() || 'Shoot',
          owner: 'portal' as const,
          phase: 'production' as const,
          start: first?.date ?? '',
        }]
      : []),
    { title: 'First cuts for review', owner: 'portal', phase: 'delivery', start: '' },
    { title: 'Delivery', owner: 'portal', phase: 'delivery', start: '' },
  ]
}

export function countLabel(detail: KindDetail, pieces: string): string {
  if (detail.ongoing) return `Ongoing · ${detail.count} to start`
  return countOf(detail.count, pieces)
}

export type StageKey = 'pre' | 'production' | 'post'

export const STAGES: { key: StageKey; label: string }[] = [
  { key: 'pre', label: 'Pre-production' },
  { key: 'production', label: 'Production' },
  { key: 'post', label: 'Post-production' },
]

export const START = 0
export const FIRST_QUESTION = 1
export const FIRST_DETAIL = 3

/**
 * THE STEP NUMBERS.
 *
 * The detail screens are the kinds that have one — everything except
 * `other`, which instead spends one screen asking WHICH extras, then one
 * screen per extra ticked. A job with Films + cutdowns + a stream runs:
 * films, which-ones, cutdowns, stream. The counter in the eyebrow says
 * "2 of 4", because to the person filling it in they are all just things
 * being made.
 */
export const mainKinds = (a: SetupAnswers) => a.kinds.filter((kind) => kind !== 'other')
export const hasExtras = (a: SetupAnswers) => a.kinds.includes('other')
/** The "which ones?" screen. Only reachable when Something else is ticked. */
export const extrasStep = (a: SetupAnswers) => FIRST_DETAIL + mainKinds(a).length
export const firstExtraStep = (a: SetupAnswers) => extrasStep(a) + 1
/** How many things are being made, for the "2 of 4" counter. */
export const madeCount = (a: SetupAnswers) => mainKinds(a).length + (hasExtras(a) ? a.extras.length : 0)

export const duringStep = (a: SetupAnswers) =>
  FIRST_DETAIL + mainKinds(a).length + (hasExtras(a) ? 1 + a.extras.length : 0)
export const watchingStep = (a: SetupAnswers) => duringStep(a) + 1
export const tellStep = (a: SetupAnswers) => duringStep(a) + 2
export const finishStep = (a: SetupAnswers) => duringStep(a) + 3

export function stageOf(step: number, answers: SetupAnswers): StageKey {
  if (step < duringStep(answers)) return 'pre'
  if (step === duringStep(answers)) return 'production'
  return 'post'
}

export type JumpTarget = 'who' | 'making' | 'during' | 'watching' | 'tell'

export function blocksFromAnswers(answers: SetupAnswers): BlockKey[] {
  const on: BlockKey[] = []
  const has = (kind: KindKey) => answers.kinds.includes(kind)
  const extraPieces = has('other') ? piecesFromExtras(answers).length : 0

  if (answers.outline.trim()) on.push('outline')
  if (has('films') || has('event') || has('podcast') || extraPieces > 0) on.push('films')
  if (has('photography')) on.push('photographs')
  if (has('podcast')) on.push('transcripts')
  on.push('downloads')
  if (allDays(answers).length > 0) on.push('dates')
  if (answers.during.includes('plan')) on.push('plan')
  on.push('report')

  return Array.from(new Set(on))
}
